import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Zap, Shield, Target } from "lucide-react";
import { motion } from "framer-motion";
import ArrowDisplay from "@/components/ArrowDisplay";
import { fadeInUpLarge, transitionMedium, transitionSlow } from "@/utils/motion";
import { play as sfx, playHover } from "@/utils/sounds";

const reinforceSequence = ["up", "down", "right", "left", "up"];
const briefingLines = [
  "Stratagem Input Drill Console",
  "Democracy Response Console",
  "Super Earth Tactics Uplink",
  "Freedom Allocation Terminal",
  "Orbital Command Line",
  "Reinforcement Readiness Grid",
  "Liberation Signal Array",
  "Helldiver Input Calibration",
];

const flavorMessages = [
  "Remember: A dead Helldiver can't spread Democracy.",
  "Fun fact: 97% of friendly fire incidents involve confused fingers.",
  "High Command reminds you: Hesitation is treason.",
  "Your drill sergeant believes in you. Don't let them down.",
  "Every millisecond counts when bugs are eating your squad.",
  "Muscle memory > Panic screaming into comms.",
  "The Automatons aren't waiting for you to remember the code.",
  "Super Earth didn't train you to fumble stratagems.",
  "A well-timed Reinforce saves nine... lives.",
  "Speed is democracy. Accuracy is freedom.",
  "Your teammates' respawn timer depends on you.",
  "Practice now, or explain to High Command later.",
  "The only acceptable input speed is 'instant'.",
  "Bugs don't give you time to look at your keyboard.",
  "Each perfect combo brings us closer to Managed Democracy.",
  "Your fingers are weapons. Train them accordingly.",
  "SEAF Command has logged your training session.",
  "Liberate your muscle memory from hesitation.",
  "Warning: Unpatriotic input delays may result in casualties.",
  "Drill until the code becomes second nature.",
];

const pickLine = () => briefingLines[Math.floor(Math.random() * briefingLines.length)];
const pickFlavor = () => flavorMessages[Math.floor(Math.random() * flavorMessages.length)];

const Home = () => {
  const [briefingLine, setBriefingLine] = useState(pickLine());
  const [flavorText, setFlavorText] = useState(pickFlavor());

  useEffect(() => {
    setBriefingLine(pickLine());
    setFlavorText(pickFlavor());
  }, []);
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 md:px-12" data-testid="home-page">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
        <motion.section
          className="lg:col-span-7 space-y-8"
          {...fadeInUpLarge}
          transition={transitionMedium}
        >
          <div className="hud-card clip-corners p-8" data-testid="home-hero-card">
            <p
              className="text-xs uppercase tracking-[0.4em] text-gray-400"
              data-testid="home-hero-kicker"
            >
              {briefingLine}
            </p>
            <h1
              className="mt-3 font-headings text-5xl md:text-6xl uppercase tracking-widest text-[#FFE800]"
              data-testid="home-hero-title"
            >
              Type faster. Call faster.
            </h1>
            <p className="mt-4 text-lg text-gray-300" data-testid="home-hero-subtitle">
              Train your fingers for perfect stratagem execution and put freedom on the ground exactly
              where it belongs.
            </p>
            <p className="mt-3 text-sm italic text-[#FFE800]/70" data-testid="home-flavor-text">
              &ldquo;{flavorText}&rdquo;
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                to="/practice?target=reinforce"
                onClick={() => sfx("click")}
                onMouseEnter={playHover}
                className="bg-[#FFE800] text-black font-headings text-xl uppercase tracking-widest px-8 py-3 clip-corners hover:bg-[#FFD700] hover:scale-[1.02] transition-all"
                data-testid="home-start-reinforce-button"
              >
                Start Reinforce Drill
              </Link>
              <Link
                to="/library"
                onClick={() => sfx("click")}
                onMouseEnter={playHover}
                className="border border-white/20 text-white font-headings text-xl uppercase tracking-widest px-8 py-3 hover:border-[#FFE800] hover:text-[#FFE800] transition-all"
                data-testid="home-library-button"
              >
                Browse Stratagems
              </Link>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              { icon: Zap, label: "Speed Control", text: "Dial in inputs-per-minute like a pro." },
              { icon: Shield, label: "Perfect Only", text: "No accuracy, no stratagem. Build consistency." },
              { icon: Target, label: "Session Metrics", text: "Track accuracy, streak, and best time." },
            ].map((item, index) => (
              <div
                key={item.label}
                className="hud-card clip-corners p-4"
                data-testid={`home-feature-${index}`}
              >
                <item.icon className="h-6 w-6 text-[#FFE800]" />
                <h3 className="mt-2 font-headings text-xl uppercase tracking-wide text-white">
                  {item.label}
                </h3>
                <p className="text-sm text-gray-400" data-testid={`home-feature-${index}-text`}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </motion.section>
        <motion.section
          className="lg:col-span-5 space-y-6"
          {...fadeInUpLarge}
          transition={transitionSlow}
        >
          <div className="hud-card clip-corners p-6" data-testid="home-reinforce-card">
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Most common stratagem</p>
            <h2 className="mt-2 font-headings text-3xl uppercase tracking-widest text-white">
              Reinforce Drill
            </h2>
            <div className="mt-4">
              <ArrowDisplay sequence={reinforceSequence} progress={0} testIdPrefix="home-reinforce" />
            </div>
            <p className="mt-4 text-sm text-gray-400" data-testid="home-reinforce-note">
              Every miss resets the combo. Lock in the sequence and make the squad stop teasing you.
            </p>
          </div>
          <div className="hud-card clip-corners p-6" data-testid="home-command-card">
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Command Brief</p>
            <h3 className="mt-2 font-headings text-2xl uppercase tracking-wide text-white">
              Your training loop
            </h3>
            <ol className="mt-4 space-y-3 text-sm text-gray-400">
              <li data-testid="home-command-step-1">1. Choose a stratagem from the catalog.</li>
              <li data-testid="home-command-step-2">2. Input the code perfectly to score.</li>
              <li data-testid="home-command-step-3">3. Watch your speed climb in War Log stats.</li>
            </ol>
            <Link
              to="/practice"
              className="mt-5 inline-flex items-center gap-2 text-[#FFE800] font-headings uppercase tracking-[0.2em]"
              data-testid="home-command-cta"
            >
              Begin drills <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Home;